import {
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  type TableContainerProps,
} from '@shared/components';
import { TableHeader } from '@shared/table-generator';

import { useItemAnalysisSearchParams } from '../../hooks';
import { useItemAnalysis } from '../../providers';
import { ItemAnalysisResponseStatisticsTableBody } from '../item-analysis-response-statistics-table-body';
import { OnRowOverlayItemAnalysisTable } from '../on-row-overlay-item-analysis-table';

import { ItemAnalysisResponseStatisticsTableHeader } from './item-analysis-response-statistics-table-header';

export default function ItemAnalysisResponseStatisticsTable(props: Readonly<TableContainerProps>) {
  const { ...restProps } = props;
  const { questionAnalysisData, searchItemAnalysisLoading, isFilterSet } = useItemAnalysis();
  const [{ page, pageSize }, { changePage, changePageSize }] = useItemAnalysisSearchParams();
  const data = questionAnalysisData?.records ?? [];
  const total = questionAnalysisData?.total ?? 0;
  const choiceNumber = data.reduce((max, item) => Math.max(max, item.numbersOfExaminees.length), 0);
  return (
    <TableContainer
      {...restProps}
      sx={{
        borderRadius: (theme) => theme.shape.rounded(2),
        overflowX: 'auto',
        border: '1px solid',
        borderColor: (theme) => theme.palette.grey[300],
        ...restProps.sx,
      }}
    >
      <Table
        stickyHeader
        size='small'
        sx={{
          border: 'none',
          borderCollapse: 'initial',
          mb: '-1px',
        }}
      >
        <TableHeader
          columns={ItemAnalysisResponseStatisticsTableHeader(choiceNumber)}
          slotProps={{
            tableHeader: {
              sx: (theme) => ({
                bgcolor: theme.palette.common.black,
                borderColor: theme.palette.grey[300],
                border: 'none',
              }),
            },
            tableHeaderRow: {
              sx: {
                ':first-of-type': {
                  'th:last-of-type': {
                    borderRight: 'none',
                  },
                },
                ':last-of-type > th': {
                  borderTop: '1px solid',
                  borderColor: (theme) => theme.palette.grey[300],
                },
              },
            },
            tableHeaderCell: {
              sx: (theme) => ({
                ...theme.typography.body1Semibold,
                color: theme.palette.common.white,
                backgroundColor: theme.palette.common.black,
                padding: theme.spacing(3),
                borderRight: '1px solid',
                borderBottom: 'none',
                borderColor: theme.palette.grey[300],
              }),
            },
          }}
        />
        {total === 0 ? null : (
          <>
            <TableBody>
              <ItemAnalysisResponseStatisticsTableBody rows={data} />
            </TableBody>
            <TablePagination
              count={total}
              page={page}
              onPageChange={(event, newPage) => changePage(newPage)}
              rowsPerPageOptions={[50, 100, 150, 200]}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(event) => changePageSize(Number(event.target.value))}
            />
          </>
        )}
      </Table>
      <OnRowOverlayItemAnalysisTable
        total={total}
        isLoading={searchItemAnalysisLoading}
        isFilterSet={isFilterSet}
      />
    </TableContainer>
  );
}
